import { LessonEntity } from '@server/schemas'
import type { Locale } from '@shared/config/i18n'
import { DEFAULT_TIMEZONE } from '@shared/libs/dayjs'
import { ComponentProps } from 'react'

import { BadgeV1 } from '@podo-app/design-system-temp'

import { CLASS_STATUS_MAP, type ClassStatus, ERROR_MESSAGE_MAP, type LessonState } from '../../model/constants/card'
import type { MinimalLessonEntity } from '../../model/types/view-model'
import {
  calculateLessonState,
  calculateLessonStatus,
  calculatePreStudyProgress,
  calculateTrialLessonStatus,
  formatLessonTime,
  getStatusColor,
  hasLessonHistory as checkHasLessonHistory,
  isBookingEnabled,
  isChangeNotAllowedForLesson,
  needsMicrophonePermission,
} from '../helpers/card'
import { LessonBookingPolicy } from '../policies/booking'

export class LessonViewModel {
  constructor(
    private readonly lessonEntity: MinimalLessonEntity | LessonEntity,
    private readonly bookingPolicy: LessonBookingPolicy,
    private readonly timezone: string = DEFAULT_TIMEZONE,
    private readonly locale: Locale = 'ko',
  ) {}

  // 인스턴스 속성
  public readonly CLASS_STATUS_MAP = CLASS_STATUS_MAP
  public readonly ERROR_MESSAGE_MAP = ERROR_MESSAGE_MAP

  get entity() {
    return this.lessonEntity
  }

  get hasLessonHistory(): boolean {
    return checkHasLessonHistory(this.lessonEntity.classCount, this.lessonEntity.compClassCount)
  }

  get status(): ClassStatus {
    return calculateLessonStatus(this.lessonEntity)
  }

  get trialStatus(): ClassStatus {
    return calculateTrialLessonStatus(this.lessonEntity)
  }

  get statusColor(): ComponentProps<typeof BadgeV1>['variant'] {
    return getStatusColor(this.status)
  }

  get bookingEnabled(): boolean {
    return isBookingEnabled(this.lessonEntity.preStudyTime)
  }

  get progress(): number {
    return calculatePreStudyProgress(this.lessonEntity.preStudyTime)
  }

  get isChangeNotAllowed(): boolean {
    return isChangeNotAllowedForLesson(this.lessonEntity.unixClassStartDatetime)
  }

  get permission(): boolean {
    return needsMicrophonePermission(this.lessonEntity.teacherId, this.lessonEntity.unixClassStartDatetime)
  }

  get formattedTime(): Nullable<string> {
    return formatLessonTime(
      this.lessonEntity.unixClassStartDatetime,
      this.lessonEntity.unixClassEndDatetime,
      this.lessonEntity.teacherName,
      this.timezone,
      this.locale,
    )
  }

  get lessonState(): LessonState {
    return calculateLessonState(this.lessonEntity.preStudyTime, this.lessonEntity.compClassCount)
  }

  async bookingValidation() {
    return this.bookingPolicy.validateBooking()
  }

  get hasDuplicateBooking(): boolean {
    return this.bookingPolicy.hasDuplicateBooking
  }

  get regClassCountByLanguage() {
    return this.bookingPolicy.regClassCountByLanguage
  }

  get remainTicketCount(): number {
    return this.bookingPolicy.remainTicketCount
  }
}
